import { ERROR_CODE_INVALID_LIST_VALUE } from '@global/constants/cellValidationErrors'
import { GovernanceType } from '@global/types'
import { decodeURI } from '@utils/link'
import { chipsToString } from 'handsontable/renderers/renderers'
import { Column, ErrorsByType, Row, RowProperties } from './types'
import { RowPropertyName } from '../../readGridToolbar/components/toggleColumns/types'
import { TYPE_ERROR, TYPE_INFO, TYPE_WARNING } from '../errors/ErrorsUtil'

interface FirstSelectRowArgs {
  selectedCells: Record<string, boolean>
  rowIndex: number
  columnIndex: number
}

/**
 * Identify the first row from the selection.
 * - Used to apply even row styling.
 * @param {*} args
 * @param {Object} args.selectedCells The selected cells in the range; e.g. { 7_2: true }
 * @param {Number} args.rowIndex Cell row index.
 * @param {Number} args.columnIndex Cell column index.
 * @returns {boolean} Is this cell the first selected in the row?
 */
export function isFirstSelectedRow({
  selectedCells,
  rowIndex,
  columnIndex,
}: FirstSelectRowArgs): boolean {
  const selectedCellKeys = Object.keys(selectedCells) || []
  const [firstSelectedCellRow, firstSelectedCellColumn] = selectedCellKeys[0]?.split('_') ?? []

  if (!firstSelectedCellRow || !firstSelectedCellColumn) {
    return false
  }

  const isFirstSelectedCell =
    rowIndex === +firstSelectedCellRow && columnIndex === +firstSelectedCellColumn

  return isFirstSelectedCell
}

/**
 * A collection of list values that are invalid for the cell.
 */
export function getInvalidListValues({ cellData }) {
  const invalidListValueError = cellData?.errors?.find(
    err => err.errorCode === ERROR_CODE_INVALID_LIST_VALUE,
  )
  const invalidListValues = invalidListValueError?.errorData?.listValues || []

  return invalidListValues
}

export function getSplitCellValue(
  featureChipsUILists: boolean,
  cellData?: { value: unknown },
  listDelimiter?: string,
) {
  if (!featureChipsUILists) return null
  if (typeof cellData?.value !== 'string') return null
  if (!listDelimiter) return null

  return cellData.value.split(listDelimiter)
}

export function getShowChipsTooltip(featureChipsUILists: boolean, column: Column): boolean {
  return featureChipsUILists && column.governanceType === 'list' && Boolean(column?.listDelimiter)
}

export function getDataTip(showChipsTooltip: boolean, splitCellValue, cellData) {
  return showChipsTooltip && cellData?.value
    ? chipsToString({
        chips: splitCellValue,
        invalidListValues: getInvalidListValues({ cellData }),
      })
    : null
}

export function getRowType(index: number, isRowProperty: boolean): string {
  const type = index % 2 === 0 ? 'even' : 'odd'

  return isRowProperty ? `${type}-property` : type
}

export function getErrorClass(groupedByType: ErrorsByType, hasComment: boolean): string {
  if (groupedByType[TYPE_ERROR].length > 0 || hasComment) return TYPE_ERROR
  if (groupedByType[TYPE_WARNING].length > 0) return TYPE_WARNING
  if (groupedByType[TYPE_INFO].length > 0) return TYPE_INFO

  return ''
}

interface ExtractLowestLevelValuesProps {
  obj: Record<string, any>
  parents?: string[]
}

interface ExtractLowestLevelValuesResult {
  value: string[]
  parents: string[]
}

/**
 *
 * This function recursively iterates through an object and returns the lowest level values.
 * and returns an array of objects containing the key of parents objects.
 *
 * @param obj { [key: string]: any }
 * @param parents string[]
 * @returns [ value: string, parents: string[] ]
 *
 * @example
 *
 * const obj = {
 *  a: {
 *   b: {
 *    c: 'value'
 *    d: 'value 2'
 *   }
 * }
 *
 * const result = extractLowestLevelValues({ obj })
 *
 * console.log(result)
 *
 * // [
 * //   {
 * //     value: 'value, value 2',
 * //     parents: ['a', 'b']
 * //   }
 * // ]
 */
function extractLowestLevelValues({ obj, parents = [] }: ExtractLowestLevelValuesProps) {
  let result: ExtractLowestLevelValuesResult[] = []

  if (Array.isArray(obj)) {
    // If it's an array, treat it as a single value and add it to the result
    result.push({
      value: obj as string[],
      parents: [...parents],
    })
  } else if (typeof obj === 'object' && obj !== null) {
    // If it's an object, iterate through each key
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        // Recursively call the function for each key
        const nestedValues = extractLowestLevelValues({ obj: obj[key], parents: [...parents, key] })
        result = result.concat(nestedValues)
      }
    }
  } else if (obj !== undefined) {
    // Check for undefined values
    // If it's neither an array nor an object, it's the lowest level value
    result.push({
      value: obj as string[],
      parents: [...parents],
    })
  }

  return result
}

export function getCellData({
  row,
  fieldId,
  property,
  properties,
  governanceType,
  isClassificationColumn,
}: {
  row: Row
  fieldId: number
  property?: string
  properties?: RowProperties
  governanceType: GovernanceType
  isClassificationColumn?: boolean
}) {
  if (governanceType === 'cv_id' || property === RowPropertyName.cvId) {
    return {
      ...row?.data?.[fieldId],
      value: row?.cvid,
    }
  }

  if (isClassificationColumn) {
    const categories = row?.classification?.summary?.categories || []
    const { tags } = categories.find(({ categoryId }) => categoryId === fieldId) || {}

    if (!tags) return { value: '' }

    const classificationsValues = extractLowestLevelValues({ obj: tags })

    return {
      value: {
        hasNestedValues: true,
        classificationsValues,
      },
    }
  }

  if (property) {
    return { value: properties?.[property] }
  }

  // If the governance type is a link, we need to decode the value
  // because some special characters are encoded
  if (governanceType === 'link' && row?.data?.[fieldId]?.value) {
    return { value: decodeURI(row?.data?.[fieldId]?.value), ...row?.data?.[fieldId] }
  }

  return row?.data?.[fieldId]
}
